import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CheckIcon from '@material-ui/icons/Check';
import {
  IJurisdiction,
  JURISDICTION_GROUPS,
  JurisdictionTag,
  findJurisdiction,
} from '../types/jurisdiction';
import { setAllowedJurisdictions, setJurisdiction } from '../store/jurisdiction/action';
import { IJurisdictionState } from '../store/jurisdiction/types';
import { auth } from '../services/firebase';
import { fetchAllowedJurisdictions } from '../services/jurisdiction';

const Wrapper = styled.div`
  width: calc(100% - 20px);
  margin: 12px 10px 4px;
  position: relative;
`;

const Label = styled.div`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #546170;
  padding: 0 4px 6px;
`;

const Trigger = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  background: ${(p) =>
    p.$open
      ? 'linear-gradient(90deg, rgba(10, 72, 56, 0.82) 0%, rgba(12, 60, 47, 0.82) 100%)'
      : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid
    ${(p) => (p.$open ? 'rgba(52, 211, 153, 0.35)' : 'rgba(255, 255, 255, 0.08)')};
  color: #f8fafc;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  text-align: left;

  &:hover {
    border-color: rgba(52, 211, 153, 0.35);
    background: linear-gradient(90deg, rgba(10, 72, 56, 0.6) 0%, rgba(12, 60, 47, 0.6) 100%);
  }

  &:focus {
    outline: none;
    border-color: rgba(52, 211, 153, 0.55);
  }
`;

const TriggerFlag = styled.span`
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;

const TriggerName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled(KeyboardArrowDownIcon)<{ $open: boolean }>`
  color: #94a3b8 !important;
  font-size: 18px !important;
  transition: transform 0.15s ease;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: rgba(15, 23, 32, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  padding: 6px;
  z-index: 1000;
  max-height: 380px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(52, 211, 153, 0.35) rgba(15, 23, 42, 0.5);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(52, 211, 153, 0.35);
    border-radius: 4px;
  }
`;

const GroupLabel = styled.div`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #546170;
  padding: 10px 10px 6px;
`;

const Option = styled.button<{ $active: boolean; $disabled: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.18)' : 'transparent')};
  border-radius: 8px;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  color: #cbd5e1;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  &:hover {
    ${(p) =>
      !p.$disabled &&
      `
      background: rgba(52, 211, 153, 0.06);
      border-color: rgba(52, 211, 153, 0.14);
      color: #f8fafc;
    `}
  }

  &:focus {
    outline: none;
  }
`;

const OptionFlag = styled.span`
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
`;

const OptionName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagPill = styled.span<{ $variant: JurisdictionTag }>`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  padding: 3px 7px;
  border-radius: 6px;
  line-height: 1;
  flex-shrink: 0;
  ${(p) => {
    switch (p.$variant) {
      case 'Full':
        return 'background: rgba(52, 211, 153, 0.18); color: #34d399;';
      case 'Growing':
        return 'background: rgba(250, 204, 21, 0.18); color: #facc15;';
      case 'Foundational':
        return 'background: rgba(148, 163, 184, 0.18); color: #cbd5e1;';
      case 'Soon':
        return 'background: rgba(148, 163, 184, 0.12); color: #94a3b8;';
      default:
        return '';
    }
  }}
`;

const ActiveCheck = styled(CheckIcon)`
  color: #34d399 !important;
  font-size: 16px !important;
  flex-shrink: 0;
`;

const JurisdictionSelector: React.FC = () => {
  const dispatch = useDispatch();
  const activeId = useSelector<{ jurisdiction: IJurisdictionState }, IJurisdictionState['activeId']>(
    (s) => s.jurisdiction.activeId
  );
  const allowedIds = useSelector<
    { jurisdiction: IJurisdictionState },
    IJurisdictionState['allowedIds']
  >((s) => s.jurisdiction.allowedIds);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const active = useMemo(() => findJurisdiction(activeId), [activeId]);

  // Load the jurisdictions this user is allowed to select once they're signed in.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) return;
      fetchAllowedJurisdictions()
        .then((ids) => dispatch(setAllowedJurisdictions(ids)))
        .catch(() => {
          // On failure keep the restrictive default (US-only) already in the store.
        });
    });
    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // An option is selectable only if it isn't statically disabled AND the current
  // user is allowed to access it.
  const isItemDisabled = (item: IJurisdiction) => !!item.disabled || !allowedIds.includes(item.id);

  const handleSelect = (item: IJurisdiction) => {
    if (isItemDisabled(item)) return;
    dispatch(setJurisdiction(item.id));
    setOpen(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Label>Jurisdiction</Label>
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        $open={open}
        onClick={() => setOpen((v) => !v)}
      >
        <TriggerFlag aria-hidden>{active.flag}</TriggerFlag>
        <TriggerName>{active.name}</TriggerName>
        {active.tag && <TagPill $variant={active.tag}>{active.tag}</TagPill>}
        <Chevron $open={open} />
      </Trigger>

      {open && (
        <Panel role="listbox">
          {JURISDICTION_GROUPS.map((group) => (
            <div key={group.id}>
              <GroupLabel>{group.label}</GroupLabel>
              {group.items.map((item) => {
                const isActive = item.id === activeId;
                const isDisabled = isItemDisabled(item);
                return (
                  <Option
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    $active={isActive}
                    $disabled={isDisabled}
                    onClick={() => handleSelect(item)}
                  >
                    <OptionFlag aria-hidden>{item.flag}</OptionFlag>
                    <OptionName>{item.name}</OptionName>
                    {item.tag && <TagPill $variant={item.tag}>{item.tag}</TagPill>}
                    {isActive && <ActiveCheck />}
                  </Option>
                );
              })}
            </div>
          ))}
        </Panel>
      )}
    </Wrapper>
  );
};

export default JurisdictionSelector;
